/*
 * fête Wishlist — storefront script (replaces the Swym-backed version).
 *
 * One script runs every heart on the page, the header count, and the
 * wishlist page. It talks only to our own app through Shopify's app proxy
 * (/apps/wishlist/...), so there is no third-party code on the storefront.
 *
 * Who stores what:
 *   - Logged-in customers: the list lives in their Shopify customer record
 *     (metafield fete_wishlist.items), so it follows them across devices.
 *     The page seeds it from Liquid (snippets/fete-wishlist-config.liquid),
 *     so no request is needed just to paint hearts.
 *   - Guests: the list lives in this browser (localStorage). When they log
 *     in, it is merged into their account automatically.
 *
 * Markup contract (snippets/fete-wishlist-button.liquid):
 *   <button data-fete-wishlist data-empi="<product id>" data-epi="<variant id>"
 *           data-product-title="…" data-product-url="…" data-product-image="…"
 *           data-product-price="…" data-product-vendor="…" hidden>
 *   Buttons start hidden and are revealed once this script runs, so a
 *   broken script never leaves dead hearts on the page.
 *
 * Header count: any element with [data-fete-wishlist-count].
 * Events: dispatches "fete:wishlist-change" on document with { items }.
 * API: window.FeteWishlist = { ready, has(id), toggle(btn), items(), share(), stopSharing() }.
 */
(function () {
  if (window.__feteWishlistInit) return;
  window.__feteWishlistInit = true;

  var LOCAL_KEY = 'fete:wishlist:v1';
  var LIMIT = 250;

  var config = readConfig();
  var PROXY = config.proxy || '/apps/wishlist';
  var loggedIn = !!config.loggedIn;
  var items = []; // [{ p, v?, a }]
  var pending = {}; // product id -> true while a save is in flight
  var lastSeq = 0; // numbers each save so a late reply to an older one is ignored

  // ---------- config & storage ----------

  function readConfig() {
    var el = document.getElementById('fete-wishlist-config');
    if (!el) return {};
    try { return JSON.parse(el.textContent) || {}; } catch (e) { return {}; }
  }

  function readLocal() {
    try {
      var raw = JSON.parse(window.localStorage.getItem(LOCAL_KEY) || '[]');
      return Array.isArray(raw) ? raw.filter(validItem).slice(0, LIMIT) : [];
    } catch (e) { return []; }
  }

  function writeLocal(list) {
    try { window.localStorage.setItem(LOCAL_KEY, JSON.stringify(list.slice(0, LIMIT))); } catch (e) { /* private mode: list lasts for this page only */ }
  }

  function clearLocal() {
    try { window.localStorage.removeItem(LOCAL_KEY); } catch (e) { /* ignore */ }
  }

  function validItem(i) {
    return i && typeof i === 'object' && /^\d{1,20}$/.test(String(i.p));
  }

  function normalize(list) {
    return (Array.isArray(list) ? list : []).filter(validItem).map(function (i) {
      var out = { p: Number(i.p), a: i.a || new Date().toISOString() };
      if (i.v && /^\d{1,20}$/.test(String(i.v))) out.v = Number(i.v);
      return out;
    });
  }

  // ---------- network ----------

  function api(path, body) {
    var opts = { credentials: 'same-origin', headers: { Accept: 'application/json' } };
    if (body) {
      opts.method = 'POST';
      opts.headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(body);
    }
    return fetch(PROXY + path, opts).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (data) {
        if (!res.ok) {
          var err = new Error(data.error || 'Request failed');
          err.status = res.status;
          throw err;
        }
        return data;
      });
    });
  }

  // ---------- state ----------

  function has(productId) {
    var id = Number(productId);
    for (var i = 0; i < items.length; i++) if (items[i].p === id) return true;
    return false;
  }

  function setItems(next) {
    items = normalize(next);
    if (!loggedIn) writeLocal(items);
    paintAll();
    document.dispatchEvent(new CustomEvent('fete:wishlist-change', { detail: { items: items.slice() } }));
  }

  // ---------- UI ----------

  function paint(btn) {
    var on = has(btn.dataset.empi);
    var title = btn.dataset.productTitle || '';
    // Only write what actually changed, so repainting never triggers the
    // mutation observer below again.
    if (btn.hidden) btn.hidden = false;
    setAttr(btn, 'aria-pressed', on ? 'true' : 'false');
    btn.classList.toggle('fete-wishlist--on', on);
    setAttr(btn, 'aria-label', (on ? 'Remove ' : 'Save ') + title + (on ? ' from wishlist' : ' to wishlist'));
  }

  function setAttr(el, name, value) {
    if (el.getAttribute(name) !== value) el.setAttribute(name, value);
  }

  function paintAll() {
    document.querySelectorAll('[data-fete-wishlist]').forEach(paint);
    var text = items.length ? String(items.length) : '';
    document.querySelectorAll('[data-fete-wishlist-count]').forEach(function (el) {
      if (el.textContent !== text) el.textContent = text;
      if (el.hidden !== (items.length === 0)) el.hidden = items.length === 0;
    });
  }

  function announce(message) {
    var live = document.getElementById('fete-wishlist-live');
    if (!live) {
      live = document.createElement('div');
      live.id = 'fete-wishlist-live';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      live.className = 'visually-hidden';
      live.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;';
      document.body.appendChild(live);
    }
    live.textContent = '';
    setTimeout(function () { live.textContent = message; }, 30);
  }

  // Guests only: the server sends Klaviyo events for logged-in customers.
  // Klaviyo records this only if it already knows the visitor (e.g. from a
  // past email click or signup); otherwise it's quietly ignored.
  function trackGuest(name, btn) {
    if (loggedIn || !window.klaviyo || typeof window.klaviyo.push !== 'function') return;
    var d = btn.dataset;
    var props = { ProductID: d.empi, VariantID: d.epi, ProductName: d.productTitle, Brand: d.productVendor, WishlistCount: items.length, Source: 'fete-wishlist' };
    if (d.productUrl) props.URL = location.origin + d.productUrl;
    if (d.productImage) props.ImageURL = d.productImage.indexOf('//') === 0 ? 'https:' + d.productImage : d.productImage;
    if (d.productPrice) props.Price = Number(d.productPrice) / 100;
    try { window.klaviyo.push(['track', name, props]); } catch (e) { /* never block the heart */ }
  }

  function withChange(list, p, v, adding, prior) {
    var rest = list.filter(function (i) { return i.p !== p; });
    if (!adding) return rest;
    return [prior || { p: p, v: v, a: new Date().toISOString() }].concat(rest);
  }

  function toggle(btn) {
    var p = Number(btn.dataset.empi);
    if (!p || pending[p]) return Promise.resolve();
    var v = Number(btn.dataset.epi) || undefined;
    var adding = !has(p);
    var prior = null; // the entry being removed, so a failed removal can restore it exactly
    items.forEach(function (i) { if (i.p === p) prior = i; });

    // Optimistic: the heart fills instantly; we undo just this change if the save fails.
    setItems(withChange(items, p, v, adding));
    announce(adding ? 'Saved to your wishlist' : 'Removed from your wishlist');
    trackGuest(adding ? 'Added to Wishlist' : 'Removed from Wishlist', btn);

    if (!loggedIn) return Promise.resolve();

    pending[p] = true;
    var seq = ++lastSeq;
    return api('/items', adding ? { op: 'add', p: p, v: v } : { op: 'remove', p: p })
      .then(function (data) {
        delete pending[p];
        // Adopt the server's list only from the newest save, with nothing else in
        // flight; otherwise an older reply arriving late could undo a newer tap.
        if (seq === lastSeq && Object.keys(pending).length === 0 && data && Array.isArray(data.items)) setItems(data.items);
      })
      .catch(function (err) {
        delete pending[p];
        if (err.status === 401) {
          // Login ended. Keep only this one change in the browser (never copy the
          // whole account list onto a possibly shared device); it merges on next login.
          loggedIn = false;
          setItems(withChange(readLocal(), p, v, adding));
          announce('Your session ended. Log in again to see your full wishlist.');
          return;
        }
        setItems(withChange(items, p, v, !adding, prior));
        announce(err.status === 404 ? 'That item is no longer available' : 'Could not save. Please try again.');
      });
  }

  // One delegated listener covers every heart, including cards added later.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-fete-wishlist]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    toggle(btn);
  });

  // Theme editor re-renders and the collection grid's "Load more".
  document.addEventListener('shopify:section:load', paintAll);
  document.addEventListener('fete:cards-added', paintAll);

  // The theme re-renders the header after load (section rendering + morph,
  // which resets the count in place), and other scripts may swap cards in.
  // Either way hearts or the count end up blank, so repaint, at most once a
  // frame, whenever one is added or changed by someone else.
  var SEL = '[data-fete-wishlist],[data-fete-wishlist-count]';
  var repaintQueued = false;
  function touchesWishlist(records) {
    for (var i = 0; i < records.length; i++) {
      var r = records[i];
      var el = r.target.nodeType === 1 ? r.target : r.target.parentElement;
      if (el && el.closest && el.closest(SEL)) return true;
      for (var j = 0; j < r.addedNodes.length; j++) {
        var n = r.addedNodes[j];
        if (n.nodeType === 1 && (n.matches(SEL) || n.querySelector(SEL))) return true;
      }
    }
    return false;
  }
  if (window.MutationObserver) {
    new MutationObserver(function (records) {
      if (repaintQueued || !touchesWishlist(records)) return;
      repaintQueued = true;
      (window.requestAnimationFrame || setTimeout)(function () { repaintQueued = false; paintAll(); });
    }).observe(document.documentElement, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['hidden', 'aria-pressed', 'class'],
    });
  }

  // Keep tabs in sync for guests.
  window.addEventListener('storage', function (e) {
    if (e.key === LOCAL_KEY && !loggedIn) { items = readLocal(); paintAll(); }
  });

  // ---------- start ----------

  var ready;
  if (loggedIn) {
    var seeded = Array.isArray(config.items) ? normalize(config.items) : null;
    var local = readLocal();
    items = seeded || [];
    paintAll();

    if (local.length) {
      // Guest list from before login: fold it into the account once.
      ready = api('/items', { op: 'merge', items: local })
        .then(function (data) { clearLocal(); setItems(data.items); })
        .catch(function () { /* keep the local copy; try again next page */ });
    } else if (!seeded || !seeded.length) {
      // Also re-check an empty seed: if Liquid ever can't read the metafield it
      // renders [], and trusting that would hide a real saved list.
      // Skipped if the shopper already tapped a heart, so it can't undo that tap.
      ready = api('/items').then(function (data) { if (lastSeq === 0) setItems(data.items); }).catch(function () { paintAll(); });
    } else {
      ready = Promise.resolve();
    }
  } else {
    items = readLocal();
    paintAll();
    ready = Promise.resolve();
  }

  window.FeteWishlist = {
    ready: ready,
    loggedIn: function () { return loggedIn; },
    has: has,
    toggle: toggle,
    items: function () { return items.slice(); },
    remove: function (productId) {
      var btn = document.createElement('button');
      btn.dataset.empi = String(productId);
      return has(productId) ? toggle(btn) : Promise.resolve();
    },
    share: function () { return api('/share', { op: 'create' }).then(function (d) { return d.url; }); },
    stopSharing: function () { return api('/share', { op: 'revoke' }); },
    api: api,
  };
  document.dispatchEvent(new CustomEvent('fete:wishlist-ready'));
})();
